import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FormSelect from "../../shared/FormSelect";
import { PRIORITY_RULES } from "./fieldForm";

const PRIORITY_OPTIONS = [{ value: "", label: "Inherit" }, ...PRIORITY_RULES];

const AssignmentPriority = ({ value, disabled, onChange }) => {
    const selected = PRIORITY_OPTIONS.find((option) => option.value === (value || "")) ?? PRIORITY_OPTIONS[0];
    const { control } = useForm({ values: { overridePriorityRule: selected } });

    return (
        <FormSelect
            name="overridePriorityRule"
            label="Priority"
            control={control}
            options={PRIORITY_OPTIONS}
            placeholder="Inherit"
            disabled={disabled}
            isClearable={false}
            onSelectedHandler={(option) => onChange(option?.value ?? "")}
        />
    );
};

const AssignmentEditor = ({ assignment, label, inactive, saving, onChange }) => (
    <div className="cdes-field-assignments__editor" aria-label={`Settings for ${label}`}>
        <p className="cdes-field-assignments__editor-title">Settings for {label}</p>
        {inactive ? (
            <p className="cdes-client-form__help">
                This assignment is retained. Future extraction will not use it until this document type is active again.
            </p>
        ) : null}
        <label className="cdes-field-form__check" htmlFor={`cdes-field-required-${assignment.documentTypeKey}`}>
            <input
                id={`cdes-field-required-${assignment.documentTypeKey}`}
                type="checkbox"
                checked={assignment.required}
                onChange={(event) => onChange(assignment.documentTypeKey, { required: event.target.checked })}
                disabled={saving}
            />
            Required
        </label>
        <div className="form-input">
            <label htmlFor={`cdes-field-extract-order-${assignment.documentTypeKey}`}>Extraction order</label>
            <input
                id={`cdes-field-extract-order-${assignment.documentTypeKey}`}
                value={assignment.extractionOrder}
                onChange={(event) => onChange(assignment.documentTypeKey, { extractionOrder: event.target.value })}
                disabled={saving}
                inputMode="numeric"
            />
        </div>
        <div className="form-input">
            <label htmlFor={`cdes-field-doc-notes-${assignment.documentTypeKey}`}>Notes</label>
            <textarea
                id={`cdes-field-doc-notes-${assignment.documentTypeKey}`}
                value={assignment.notes}
                onChange={(event) => onChange(assignment.documentTypeKey, { notes: event.target.value })}
                disabled={saving}
                rows={2}
                placeholder="Optional…"
            />
        </div>
        <details className="cdes-field-form__details">
            <summary>Override defaults for {label}</summary>
            <div className="form-input">
                <label htmlFor={`cdes-field-override-where-${assignment.documentTypeKey}`}>Where to look</label>
                <textarea
                    id={`cdes-field-override-where-${assignment.documentTypeKey}`}
                    value={assignment.overrideWhereToLook}
                    onChange={(event) => onChange(assignment.documentTypeKey, { overrideWhereToLook: event.target.value })}
                    disabled={saving}
                    rows={2}
                    placeholder="e.g. Page 1 header, opening paragraph, fee schedule table"
                />
            </div>
            <div className="form-input">
                <label htmlFor={`cdes-field-override-examples-${assignment.documentTypeKey}`}>Examples</label>
                <textarea
                    id={`cdes-field-override-examples-${assignment.documentTypeKey}`}
                    value={assignment.overrideExamples}
                    onChange={(event) => onChange(assignment.documentTypeKey, { overrideExamples: event.target.value })}
                    disabled={saving}
                    rows={2}
                    placeholder={"One example value per line.\ne.g.\nAcme Corp\nBeta Inc."}
                />
            </div>
            <div className="form-input">
                <label htmlFor={`cdes-field-override-notes-${assignment.documentTypeKey}`}>Extraction notes</label>
                <textarea
                    id={`cdes-field-override-notes-${assignment.documentTypeKey}`}
                    value={assignment.overrideExtractionNotes}
                    onChange={(event) => onChange(assignment.documentTypeKey, { overrideExtractionNotes: event.target.value })}
                    disabled={saving}
                    rows={2}
                    placeholder="Additional context, edge cases, disambiguation hints…"
                />
            </div>
            <div className="form-input">
                <label htmlFor={`cdes-field-override-format-${assignment.documentTypeKey}`}>Format pattern</label>
                <input
                    id={`cdes-field-override-format-${assignment.documentTypeKey}`}
                    value={assignment.overrideFormatPattern}
                    onChange={(event) => onChange(assignment.documentTypeKey, { overrideFormatPattern: event.target.value })}
                    disabled={saving}
                    placeholder="e.g. YYYY-MM-DD  /  Dollar amount PEPM (no $ sign)"
                />
            </div>
            <AssignmentPriority
                value={assignment.overridePriorityRule}
                disabled={saving}
                onChange={(overridePriorityRule) => onChange(assignment.documentTypeKey, { overridePriorityRule })}
            />
            <div className="form-input">
                <label htmlFor={`cdes-field-override-confidence-${assignment.documentTypeKey}`}>Confidence threshold</label>
                <input
                    id={`cdes-field-override-confidence-${assignment.documentTypeKey}`}
                    value={assignment.overrideConfidenceThreshold}
                    onChange={(event) => onChange(assignment.documentTypeKey, { overrideConfidenceThreshold: event.target.value })}
                    disabled={saving}
                    inputMode="decimal"
                    placeholder="e.g. 0.8"
                />
            </div>
            <p className="cdes-client-form__help">Leave an override blank to inherit the field default. Common labels stay on the field.</p>
        </details>
    </div>
);

const FieldAssignments = ({ assignments, typeByKey, saving, errors, onChange }) => {
    const [activeKey, setActiveKey] = useState(() => assignments.find((assignment) => assignment.selected)?.documentTypeKey ?? "");

    useEffect(() => {
        const active = assignments.find((assignment) => assignment.documentTypeKey === activeKey && assignment.selected);
        if (active) return;
        setActiveKey(assignments.find((assignment) => assignment.selected)?.documentTypeKey ?? "");
    }, [activeKey, assignments]);

    const toggle = (documentTypeKey, selected) => {
        onChange(documentTypeKey, { selected });
        if (selected) {
            setActiveKey(documentTypeKey);
            return;
        }
        if (activeKey !== documentTypeKey) return;
        const next = assignments.find((assignment) => assignment.documentTypeKey !== documentTypeKey && assignment.selected);
        setActiveKey(next?.documentTypeKey ?? "");
    };

    const activeAssignment = assignments.find((assignment) => assignment.documentTypeKey === activeKey && assignment.selected) ?? null;
    const activeType = activeAssignment ? typeByKey.get(activeAssignment.documentTypeKey) : null;
    const activeLabel = activeType?.displayName || activeAssignment?.documentTypeKey;

    return (
        <section>
            <p className="cdes-field-form__section">
                Document types <span className="danger">*</span>
            </p>
            <p className="cdes-client-form__help">Select the document types that should extract this field. Choose a selected type to edit its settings.</p>
            {assignments.length === 0 ? <p className="cdes-client-form__help">No active document types are available.</p> : null}
            <div className="cdes-field-assignments__list">
                {assignments.map((assignment) => {
                    const documentType = typeByKey.get(assignment.documentTypeKey);
                    const inactive = documentType?.status === "INACTIVE";
                    const label = documentType?.displayName || assignment.documentTypeKey;
                    const name = inactive ? `${label} (inactive)` : label;
                    const active = assignment.documentTypeKey === activeKey && assignment.selected;
                    return (
                        <div
                            key={assignment.documentTypeKey}
                            className={`cdes-field-assignments__item${assignment.selected ? " is-selected" : ""}${active ? " is-active" : ""}`}
                        >
                            <input
                                id={`cdes-field-doc-${assignment.documentTypeKey}`}
                                type="checkbox"
                                checked={assignment.selected}
                                onChange={(event) => toggle(assignment.documentTypeKey, event.target.checked)}
                                disabled={saving}
                            />
                            <label
                                htmlFor={`cdes-field-doc-${assignment.documentTypeKey}`}
                                onClick={(event) => {
                                    if (!assignment.selected) return;
                                    event.preventDefault();
                                    setActiveKey(assignment.documentTypeKey);
                                }}
                            >
                                {name}
                            </label>
                        </div>
                    );
                })}
            </div>
            {activeAssignment ? (
                <AssignmentEditor
                    assignment={activeAssignment}
                    label={activeLabel}
                    inactive={activeType?.status === "INACTIVE"}
                    saving={saving}
                    onChange={onChange}
                />
            ) : null}
            {errors ? (
                <p className="danger cdes-client-form__error" role="alert">
                    {errors}
                </p>
            ) : null}
        </section>
    );
};

export default FieldAssignments;
